import { useRef, useEffect, useState } from 'react';
import styles from './Card.module.scss';
import { XIcon } from '../../../../../../../../../../../../ui/icons/XIcon';
import { getDisplayedProduct } from '../../../../PreviewChapterLayout/FirstChapter/utils';
import { PenIcon } from '../../../../../../../../../../../../ui/icons/PenIcon';
import { Product } from './Product/Product';
import { useWindowResize } from '../../../../../../../../utils/hooks/useWindowResize';
import cn from 'classnames';
import { useLocalization } from '../../../../../../../../../../../../lib/hooks/useLocalization';
import { AppText } from '../../../../../../../../../../../../ui/text/AppText';
import { canShowProductInResult } from '../../../../../../utils';

const TABLET_WIDTH = 850;
const MOBILE_WIDTH = 400;

// eslint-disable-next-line max-lines-per-function
export function Card({
  chapter,
  index,
  chapterIconMedia,
  handleGoChapter,
  fonts,
  colors,
  productInformation,
  pagesFlow,
  shouldShowConnection,
  connectionLabel,
  resultProducts,
  recommendedProductIds,
  isSecondToLast,
}) {
  const {
    content,
    content: { headline, selectChapterIcon } = {},
    state,
    flow,
    chapterInnerPagesIds,
    id: chapterId,
    resultSelectedProducts = [],
    rules,
  } = chapter;

  const cardRef = useRef(null);
  const [borderHeight, setBorderHeight] = useState(null);

  const { buttonText } = useLocalization().translations.unique.components.productBasedFinder.result;
  const { windowWidth } = useWindowResize();

  const isMobile = windowWidth <= MOBILE_WIDTH;
  const isTablet = windowWidth > MOBILE_WIDTH && windowWidth <= TABLET_WIDTH;

  // Calculate adaptive border height
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isSecondToLast && cardRef.current) {
      const calculateHeight = () => {
        const currentCard = cardRef.current;
        const nextCard = currentCard.nextElementSibling;

        if (nextCard) {
          const currentHeight = currentCard.offsetHeight;
          const nextHeight = nextCard.offsetHeight;
          const gap = 9;

          // Calculate total height needed
          const totalHeight = currentHeight + nextHeight + gap;

          // eslint-disable-next-line no-magic-numbers
          const topOffset = currentHeight * 0.1;
          const finalHeight = totalHeight + topOffset;

          setBorderHeight(finalHeight);
        }
      };

      // Calculate on mount and after content changes
      calculateHeight();

      // Recalculate after images load or content changes
      // eslint-disable-next-line no-magic-numbers
      const timer = setTimeout(calculateHeight, 100);

      return () => clearTimeout(timer);
    }
  }, [isSecondToLast, resultSelectedProducts, headline?.value, windowWidth]);

  const displayedProduct = getDisplayedProduct(resultSelectedProducts, content?.productsPriority);

  const chapterIcon = chapterIconMedia.find(el => el.id === selectChapterIcon?.value);

  const newResultProducts = resultProducts?.filter(product => resultSelectedProducts?.some(el => el?.id === product?.id));

  return (
    <div
      ref={cardRef}
      className={cn(styles.chapterCard, {
        [styles.tablet]: isTablet,
        [styles.mobile]: isMobile,
        [styles.withConnection]: shouldShowConnection,
      })}
    >
      {isSecondToLast && borderHeight && <div className={styles.connectionBorder} style={{ height: `${borderHeight}px` }} />}
      {shouldShowConnection && connectionLabel && (
        <div className={cn(styles.connectionWrapper, isSecondToLast && styles.connectionWrapperSecondToLast)}>
          <div className={styles.connectionLabelWrapper}>
            <div className={styles.connectionLabel}>
              <AppText className={styles.connectionText} textValue={connectionLabel} style={{ fontFamily: fonts.body.font }} />
            </div>
          </div>
        </div>
      )}

      <div className={cn(styles.chapterHeader, isMobile && styles.chapterHeaderMobile)}>
        <span
          className={styles.indexBubble}
          style={{
            color: colors?.[fonts?.body?.colorVariant],
            borderColor: colors?.[fonts?.body?.colorVariant],
            fontFamily: fonts.body.font,
          }}
        >
          {index + 1}
        </span>
        <div
          className={styles.title}
          style={{
            color: colors?.[fonts?.body?.colorVariant],
            fontFamily: fonts.body.font,
          }}
        >
          {headline?.value}
        </div>
      </div>

      <div className={styles.cardBody} style={{ backgroundColor: colors?.backgroundColorSecond }}>
        <div className={styles.contentRow}>
          <div className={styles.iconBox}>
            {displayedProduct ? chapterIcon?.s3Url && <img src={chapterIcon?.s3Url} alt='Chapter icon' /> : <XIcon />}
          </div>

          <div className={styles.productsArea}>
            {newResultProducts.length > 0 ? (
              newResultProducts.map(product => {
                const productId = product?.productNameSection?.product?.value;
                const isRecommended = recommendedProductIds.includes(productId);

                return (
                  canShowProductInResult(product) && (
                    <Product
                      key={product?.id}
                      product={product}
                      fonts={fonts}
                      colors={colors}
                      pagesFlow={pagesFlow}
                      productInformation={productInformation}
                      isRecommended={isRecommended}
                    />
                  )
                );
              })
            ) : (
              <div className={styles.emptyDash} style={{ fontFamily: fonts.body.font }}>
                -
              </div>
            )}

            <button
              type='button'
              className={styles.editLink}
              onClick={() => handleGoChapter(chapterId, flow, rules, state, chapterInnerPagesIds?.[0])}
            >
              <PenIcon fill={colors?.primaryColor} />
              <AppText
                textValue={buttonText}
                style={{
                  color: colors?.primaryColor,
                  fontFamily: fonts.body.font,
                }}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
